<template>
  <div class="point-exchange-page">
    <!-- 導航列 -->
    <navbar title="兌換商品"></navbar>

    
    <!-- 固定區塊：分類篩選 + 提示訊息 -->
    <div class="sticky-header">
      <!-- 分類篩選 Tabs -->
      <div class="category-tabs">
        <button
          v-for="category in availableCategories"
          :key="category.categoryId"
          class="category-tab"
          :class="{ active: selectedCategoryId === category.categoryId }"
          @click="selectCategory(category.categoryId)"
        >
          {{ category.categoryName }}
        </button>
      </div>
      
      <!-- 提示訊息（只在有商品時顯示） -->
      <div v-if="availableCategories.length > 0" class="notice-banner">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
  <circle cx="12" cy="12" r="10" fill="none" stroke="#FF0000" stroke-width="2"/>
  <rect x="11" y="7" width="2" height="6" fill="#FF0000"/>
  <circle cx="12" cy="16" r="1" fill="#FF0000"/>
</svg>
        <span class="notice-text">商品清單僅供查看，兌換請至實體門市</span>
      </div>
    </div>

    <!-- Loading 狀態 -->
    <div v-if="isLoadingData" class="loading-container">
      <div class="skeleton-list">
        <div v-for="i in 3" :key="i" class="skeleton-item">
          <div class="skeleton-content">
            <div class="skeleton-line skeleton-title"></div>
            <div class="skeleton-line skeleton-desc"></div>
            <div class="skeleton-line skeleton-points"></div>
          </div>
          <div class="skeleton-image"></div>
        </div>
      </div>
    </div>

    <!-- Error 狀態 -->
    <div v-else-if="errorMsg" class="error-container">
      <div class="error-card">
        <i class="error-icon">⚠️</i>
        <p class="error-text">{{ errorMsg }}</p>
        <button class="retry-btn" @click="retryFetch">重試</button>
      </div>
    </div>

    <!-- 商品列表 -->
    <div v-else-if="filteredProducts.length > 0" class="products-list">
      <div 
        v-for="product in filteredProducts"
        :key="product.id"
        class="product-item"
      >
        <div class="product-info">
          <h3 class="product-name">{{ product.name }}</h3>
          <!-- 只在有說明時顯示 -->
          <p v-if="getProductDescription(product)" class="product-desc">
            {{ truncateDescription(product) }}
          </p>
        </div>
        <div class="product-right">
          <div class="product-points-wrapper">
            <p class="product-points">{{ getExchangePoints(product) }}點</p>
          </div>
          <!-- 只在有圖片時顯示 -->
          <div v-if="getProductImage(product)" class="product-image">
            <img :src="getProductImage(product)" :alt="product.name" class="product-img" />
          </div>
        </div>
      </div>
    </div>

    <!-- Empty 狀態 -->
    <div v-else class="empty-state">
      <p class="empty-text">目前沒有可兌換的商品</p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import navbar from '@/components/general/navbar.vue';
// eslint-disable-next-line no-unused-vars
import { fallbackToPlainText } from "@/utils/htmlSanitizer.utils";

export default {
  name: 'PointExchangePage',

  components: {
    navbar
  },
  
  data() {
    return {
      selectedCategoryId: null
    };
  },
  
  computed: {
    ...mapGetters('storeMenu', [
      'categories',
      'products',
      'combos',
      'isLoading',
      'errorMessage'
    ]),
    ...mapGetters('point', {
      exchangeProducts: 'exchangeProducts',
      pointLoading: 'isLoading',
      pointError: 'errorMessage'
    }),
    ...mapGetters('product', {
      getProductImageById: 'getProductImageById',
      getProductDescById: 'getProductDescById'
    }),
    
    /**
     * 建立商品 ID 到點數的對應表
     */
    productPointsMap() {
      const map = new Map();
      this.exchangeProducts.forEach(item => {
        map.set(item.product_id, item.points);
      });
      return map;
    },
    
    /**
     * 只顯示有可兌換商品的分類
     */
    availableCategories() {
      return this.categories.filter(category => {
        const productsInCategory = this.getProductsByCategory(category.categoryId);
        return productsInCategory.length > 0;
      });
    },
    
    /**
     * 篩選後的商品列表（只顯示有點數資料的商品）
     */
    filteredProducts() {
      if (!this.selectedCategoryId) {
        // 如果沒有選擇分類，顯示第一個有商品的分類
        if (this.availableCategories.length > 0) {
          return this.getProductsByCategory(this.availableCategories[0].categoryId);
        }
        return [];
      }
      
      return this.getProductsByCategory(this.selectedCategoryId);
    },
    
    /**
     * 綜合載入狀態
     */
    isLoadingData() {
      return this.isLoading || this.pointLoading;
    },
    
    /**
     * 綜合錯誤訊息
     */
    errorMsg() {
      return this.errorMessage || this.pointError;
    }
  },
  
  methods: {
    ...mapActions('storeMenu', ['loadMenuData']),
    ...mapActions('point', ['fetchExchangeProducts']),
    
    /**
     * 返回上一頁
     */
    goBack() {
      this.$router.back();
    },
    
    /**
     * 選擇分類
     */
    selectCategory(categoryId) {
      this.selectedCategoryId = categoryId;
    },
    
    /**
     * 根據分類 ID 取得商品（只返回有點數資料的商品）
     */
    getProductsByCategory(categoryId) {
      const category = this.categories.find(cat => cat.categoryId === categoryId);
      if (!category) {
        return [];
      }
      
      // 合併商品和套餐
      const allItems = [...this.products, ...this.combos];
      
      // 篩選：1. 屬於該分類 2. 在兌換商品列表中
      return allItems.filter(item => {
        const inCategory = category.categoryItems.includes(item.id);
        const hasPoints = this.productPointsMap.has(item.id);
        return inCategory && hasPoints;
      });
    },
    
    /**
     * 取得商品圖片 URL
     */
    getProductImage(product) {
      return this.getProductImageById(product.id);
    },
    
    /**
     * 取得商品說明（優先使用 API 資料，否則使用原始說明）
     */
    getProductDescription(product) {
      const apiDesc = this.getProductDescById(product.id);
      const raw = apiDesc || product.description || '';
      return fallbackToPlainText(raw);
    },

    /**
     * 截斷商品說明（最多顯示 30 字）
     */
    truncateDescription(product) {
      const description = this.getProductDescription(product);

      if (!description) return '';

      if (description.length > 30) {
        return description.substring(0, 30) + '…';
      }

      return description;
    },
    
    /**
     * 取得兌換點數（從 API 資料中取得）
     */
    getExchangePoints(product) {
      return this.productPointsMap.get(product.id) ?? 0;
    },
    
    /**
     * 重試取得資料
     */
    async retryFetch() {
      try {
        // 第一階段：並行載入商品資料和兌換商品列表
        await Promise.all([
          this.loadMenuData(),
          this.fetchExchangeProducts()
        ]);
        
        // 第二階段：取得可兌換商品的 ID 列表，並載入圖片和說明
        const productIds = this.exchangeProducts.map(item => item.product_id);
        
        await Promise.all([
          // 載入可兌換商品的圖片（只載入需要的商品）
          productIds.length > 0 
            ? this.$store.dispatch('product/getProductImages', productIds)
            : Promise.resolve(),
          // 載入商品說明
          this.$store.dispatch('product/ensureProductDescriptionsLoaded')
        ]);
        
        // 載入成功後，選擇第一個有商品的分類
        if (this.availableCategories.length > 0 && !this.selectedCategoryId) {
          this.selectedCategoryId = this.availableCategories[0].categoryId;
        }
      } catch (error) {
        console.error('重試失敗:', error);
      }
    }
  },
  
  async mounted() {
    try {
      // 第一階段：並行載入商品資料和兌換商品列表
      await Promise.all([
        this.loadMenuData(),
        this.fetchExchangeProducts()
      ]);
      
      // 第二階段：取得可兌換商品的 ID 列表，並載入圖片和說明
      const productIds = this.exchangeProducts.map(item => item.product_id);
      
      await Promise.all([
        // 載入可兌換商品的圖片（只載入需要的商品）
        productIds.length > 0 
          ? this.$store.dispatch('product/getProductImages', productIds)
          : Promise.resolve(),
        // 載入商品說明
        this.$store.dispatch('product/ensureProductDescriptionsLoaded')
      ]);
      
      // 選擇第一個有商品的分類
      if (this.availableCategories.length > 0 && !this.selectedCategoryId) {
        this.selectedCategoryId = this.availableCategories[0].categoryId;
      }
    } catch (error) {
      console.error('載入資料失敗:', error);
    }
  }
};
</script>

<style lang="scss" scoped>
.point-exchange-page {
  min-height: 100vh;
  background-color: #FFFFFF;
}

// 導航列
.navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: #F0F0F0;
  height: 52px;
}

.nav-content {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-left {
  position: absolute;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.back-text {
  font-size: 20px;
  color: #666666;
}

.nav-title {
  font-size: 22px;
  font-weight: 600;
  color: #FD5001;
  margin: 0;
}

// 使用者點數顯示區
.points-header {
  width: 100%;
  height: 45px;
  overflow: hidden;
}

.header-bg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

// 固定區塊容器（包含分類和提示訊息）
.sticky-header {
  position: sticky;
  top: 52px;
  z-index: 90;
  background-color: #FFFFFF;
}

// 提示訊息
.notice-banner {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  background-color: #FFFFFF;
}

.notice-text {
  font-size: 16px;
  color: #FF0000;
  flex: 1;
}

// 分類篩選 Tabs
.category-tabs {
  display: flex;
  gap: 6px;
  padding: 12px 16px;
  background-color: #FFFFFF;
  overflow-x: auto;
  white-space: nowrap;
  
  &::-webkit-scrollbar {
    display: none;
  }
}

.category-tab {
  flex-shrink: 0;
  padding: 12px 10px;
  border: none;
  border-radius: 6px;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.72px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #EEEEEE;
  color: #000000;
  
  &.active {
    background-color: #FD5001;
    color: #FFFFFF;
  }
  
  &:hover:not(.active) {
    background-color: #E0E0E0;
  }
}

// Loading 狀態
.loading-container {
  padding: 0 16px;
}

.skeleton-list {
  display: flex;
  flex-direction: column;
}

.skeleton-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px dashed #000000;
}

.skeleton-content {
  flex: 1;
  padding-right: 16px;
}

.skeleton-line {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 12px;
}

.skeleton-title {
  height: 24px;
  width: 60%;
}

.skeleton-desc {
  height: 48px;
  width: 100%;
}

.skeleton-points {
  height: 24px;
  width: 30%;
}

.skeleton-image {
  width: 120px;
  height: 120px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  flex-shrink: 0;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Error 狀態
.error-container {
  padding: 40px 20px;
}

.error-card {
  background: white;
  padding: 40px 20px;
  border-radius: 8px;
  text-align: center;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-text {
  color: #D60C0A;
  font-size: 16px;
  margin-bottom: 20px;
}

.retry-btn {
  background: #FD5001;
  color: white;
  border: none;
  padding: 10px 30px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  
  &:hover {
    background: #E64801;
  }
}

// 商品列表
.products-list {
  padding: 0 16px;
  padding-bottom: 30px;  
}

.product-item {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px dashed #000000;
  
  &:last-child {
    border-bottom: none;
  }
}

.product-info {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 20px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  line-height: 24px;
}

.product-desc {
  font-size: 16px;
  color: #666666;
  margin: 0;
  line-height: 24px;
  word-break: break-word;
}

.product-right {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.product-points-wrapper {
  display: flex;
  align-items: flex-end;
}

.product-points {
  font-size: 16px;
  color: #FD5001;
  margin: 0;
  line-height: 24px;
  white-space: nowrap;
}

.product-image {
  width: 120px;
  height: 120px;
  flex-shrink: 0;
}

.product-img {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: cover;
}

// Empty 狀態
.empty-state {
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #999;
}
</style>